import { ScheduleMetadata } from 'components/schedules/DuplicateScheduleDialog';
import {
  createFetchWorkflowResultByIds,
  createWorkflowActions,
} from '@tock/shared/workflow/actionCreators';
import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  BusinessInventorySchedule,
  BusinessInventoryScheduleUpdateRequest,
  BusinessInventoryScheduleCopyRequest,
} = protos;

export function fetch(): ApiAction {
  return {
    TockApi: {
      getListProto: '/business/inventory/schedule',
      type: AppConstants.BUSINESS_INVENTORY_SCHEDULES_FETCH_ALL,
    },
  };
}

export function fetchById(id: string): ApiAction {
  return {
    TockApi: {
      getProto: `/business/inventory/schedule/${id}`,
      type: AppConstants.BUSINESS_INVENTORY_SCHEDULES_FETCH,
    },
  };
}

export function update(
  businessInventorySchedule: Partial<dashboard.BusinessInventorySchedule>,
  publishToToday = false
): ApiAction {
  const request = BusinessInventoryScheduleUpdateRequest.fromObject({
    businessInventorySchedule,
    publishToToday,
  });
  return {
    TockApi: {
      putProto: `/business/inventory/schedule/v2`,
      request,
      type: AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE,
    },
  };
}

export function updateAsync(
  businessInventorySchedule: Partial<dashboard.BusinessInventorySchedule>,
  publishToToday = false,
  keepTimelineBlocks = false
): ApiAction {
  const request = BusinessInventoryScheduleUpdateRequest.fromObject({
    businessInventorySchedule,
    publishToToday,
    keepTimelineBlocks,
  });
  return {
    TockApi: {
      putProto: `/async/business/inventory/schedule`,
      request,
      type: AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC,
    },
  };
}

export const { fetchWorkflowStatus, fetchWorkflowResult } = createWorkflowActions({
  statusType: AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_WORKFLOW,
  resultType: AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_WORKFLOW_RESULTS,
  errorType: AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_WORKFLOW_FAILED,
});

/**
 * A clone of `fetchWorkflowResult()` that only takes the necessary arguments as a
 * list instead of a workflowInfo object (with unused properties).
 *
 * Do NOT use for any workflow that might usee GCS.
 *
 * @see admin/shared/src/js/workflow/actionCreators.ts
 */
export const fetchWorkflowResultByIds = createFetchWorkflowResultByIds(
  AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_WORKFLOW_RESULTS
);

export function create(inventorySchedule: Partial<dashboard.BusinessInventorySchedule>): ApiAction {
  return {
    TockApi: {
      postProto: '/business/inventory/schedule',
      request: BusinessInventorySchedule.fromObject(inventorySchedule),
      type: AppConstants.BUSINESS_INVENTORY_SCHEDULES_CREATE,
      toast: inventorySchedule.isSpecialDay
        ? 'Special day saved to draft'
        : 'Schedule saved to draft',
    },
  };
}

export function duplicate(
  id: number,
  copyRequest: ScheduleMetadata,
  toast = 'Schedule duplicated'
): ApiAction {
  return {
    TockApi: {
      postProto: `/business/inventory/schedule/${id}/copy`,
      request: BusinessInventoryScheduleCopyRequest.fromObject(copyRequest),
      type: AppConstants.BUSINESS_INVENTORY_SCHEDULES_DUPLICATE,
      toast,
    },
  };
}

export function remove(id: number, isSpecialDay = false): ApiAction {
  return {
    TockApi: {
      deleteProto: `/business/inventory/schedule/${id}`,
      type: AppConstants.BUSINESS_INVENTORY_SCHEDULES_DELETE,
      toast: isSpecialDay ? 'Special Day Deleted' : 'Schedule Deleted',
      context: { id },
    },
  };
}

/**
 * Download JSON export of a schedule by ID.
 */
export function exportSchedule(id: number): ApiAction {
  return {
    TockApi: {
      getRawResponse: `/business/inventory/schedule/${id}/export`,
      ignoreDispatch: true,
      type: AppConstants.NULL,
    },
  };
}

export function importSchedule(file: File): ApiAction {
  return {
    TockApi: {
      postFile: `/business/inventory/schedule/import`,
      type: AppConstants.NULL,
      request: file,
      ignoreDispatch: true,
    },
  };
}
